import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { api } from '../App';
import { Bookmark, MapPin, Clock, IndianRupee, X } from 'lucide-react';

export default function SavedOpportunities() {
  const [saved, setSaved] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchSaved = async () => {
      try {
        const response = await api.get('/saved-opportunities');
        setSaved(response.data || []);
      } catch (error) {
        console.error('Error fetching saved opportunities:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSaved();
  }, []);
  
  const unsave = async (oppId) => {
    try {
      await api.delete(`/opportunities/${oppId}/save`);
      setSaved(prev => prev.filter(opp => opp.opportunity_id !== oppId));
    } catch (error) {
      console.error('Error unsaving:', error);
    }
  };
  
  if (loading) {
    return (
      <DashboardLayout title="Saved Opportunities">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6B35]"></div>
        </div>
      </DashboardLayout>
    );
  }
  
  return (
    <DashboardLayout title="Saved Opportunities">
      {saved.length === 0 ? (
        <div className="bg-white border border-neutral-200 p-12 text-center">
          <Bookmark className="text-neutral-300 mx-auto mb-4" size={48} />
          <p className="text-neutral-500 mb-2">You haven't saved any opportunities yet</p>
          <Link
            to="/researcher/opportunities"
            className="text-[#FF6B35] font-medium hover:underline"
          >
            Browse Opportunities
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {saved.map((opp) => (
            <div
              key={opp.opportunity_id}
              data-testid={`saved-${opp.opportunity_id}`}
              className="bg-white border border-neutral-200 p-6 card-researcher"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <Link
                    to={`/opportunity/${opp.opportunity_id}`}
                    className="font-semibold text-lg text-neutral-900 hover:text-[#FF6B35] transition-colors"
                  >
                    {opp.title}
                  </Link>
                  <p className="text-sm text-neutral-500 mt-1">
                    {opp.client?.name || 'Anonymous Client'}
                  </p>
                  
                  <p className="text-neutral-600 text-sm mt-3 line-clamp-2">
                    {opp.description}
                  </p>
                  
                  <div className="flex flex-wrap gap-4 text-sm text-neutral-500 mt-4">
                    <span className="flex items-center gap-1">
                      <MapPin size={14} />
                      {opp.location_type}
                    </span>
                    {opp.duration && (
                      <span className="flex items-center gap-1">
                        <Clock size={14} />
                        {opp.duration}
                      </span>
                    )}
                    {(opp.budget_min || opp.budget_max) && (
                      <span className="flex items-center gap-1">
                        <IndianRupee size={14} />
                        ₹{opp.budget_min?.toLocaleString()} - ₹{opp.budget_max?.toLocaleString()}
                      </span>
                    )}
                  </div>
                  
                  {opp.skills_needed?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-4">
                      {opp.skills_needed.slice(0, 5).map((skill, i) => (
                        <span 
                          key={i}
                          className="text-xs px-2 py-1 bg-neutral-100 text-neutral-600 rounded-sm"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                
                <button
                  data-testid={`unsave-${opp.opportunity_id}`}
                  onClick={() => unsave(opp.opportunity_id)}
                  className="p-2 text-neutral-400 hover:text-red-500 hover:bg-red-50 transition-colors rounded-sm"
                  title="Remove from saved"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </DashboardLayout>
  );
}
